import { Badge, Checkbox, CheckedState, Table, TableColumn, Tooltip } from '@ibsys/kit';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import { Permission, Role, usePermissions } from '@/entities/roles';
import { RolesMatrixRoleHeader } from './RolesMatrixRoleHeader';
import styles from './RolesMatrix.module.css';

export type RolesMatrixPresenterProps = {
  roles: Role[];
  isChecked: (roleCode: string, permission: Permission) => boolean;
  getTogglePermission: (roleCode: string, permission: Permission) => (checked: CheckedState) => void;
  isRoleFull: (roleCode: string) => boolean;
  onToggleFullAccess: (roleCode: string, grant: boolean) => void;
  onDeleteRole: (roleCode: string) => void;
  onRenameRole: (roleCode: string, name: string) => void;
};

export const RolesMatrix: React.FC<RolesMatrixPresenterProps> = ({
  roles,
  getTogglePermission,
  isChecked,
  isRoleFull,
  onToggleFullAccess,
  onDeleteRole,
  onRenameRole,
}) => {
  const { t } = useTranslation(['roles']);

  const { data } = usePermissions();
  const permissions = data?.permissions || [];

  const allowEdit = useHasPermission('ADMIN_ROLE');

  const sortedPermissions = useMemo(
    () =>
      permissions.toSorted((a, b) =>
        a.code.replace('ADMIN_', '').replace('VIEW_', '') < b.code.replace('ADMIN_', '').replace('VIEW_', '') ? -1 : 1,
      ),
    [permissions],
  );

  // Build table columns: first column is Permission name, next columns are roles with checkboxes
  const columns = useMemo(
    (): TableColumn<Permission>[] => [
      {
        accessorKey: 'name',
        header: t('access'),
        size: 300,
        pinned: 'left',
        minSize: 300,
      },
      ...roles.map(
        (role): TableColumn<Permission> => ({
          accessorKey: role.code,
          minSize: 200,
          header: () =>
            allowEdit ? (
              <RolesMatrixRoleHeader
                roleCode={role.code}
                roleName={role.name}
                isSystem={role.isSystem}
                isFull={isRoleFull(role.code)}
                onToggleFullAccess={onToggleFullAccess}
                onDeleteRole={onDeleteRole}
                onRenameRole={onRenameRole}
              />
            ) : (
              <div className="flex items-center gap-1">
                <span>{role.name}</span>
                {role.isSystem && (
                  <Tooltip tooltip={t('systemRole')}>
                    <Badge outline className="text-xs font-light rounded-full size-2 p-2">
                      {'S'}
                    </Badge>
                  </Tooltip>
                )}
              </div>
            ),
          cell: ({ row }) => {
            const permission = row.original;
            return (
              <Checkbox
                value={isChecked(role.code, permission)}
                onChange={getTogglePermission(role.code, permission)}
                disabled={!allowEdit}
              />
            );
          },
        }),
      ),
    ],
    [t, roles, allowEdit, isRoleFull, onToggleFullAccess, onDeleteRole, onRenameRole, isChecked, getTogglePermission],
  );

  return (
    <Table
      styles={{ size: 'sm' }}
      columns={columns}
      data={sortedPermissions}
      classNames={{}}
      className={styles.rolesMatrix}
      virtualization={{ enable: false }}
    />
  );
};
